import { yupResolver } from "@hookform/resolvers/yup";
import { TrashIcon } from "@radix-ui/react-icons";
import { Badge, Button, Card, Flex, Separator, Spinner } from "@radix-ui/themes";
import { debounce, isBoolean } from "lodash";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import SelectSearchableComponent from "../../components/inputs/Select/SelectSearchableComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCtasQuery } from "../../hooks/useGetCtasQuery";
import { useGetRolesQuery } from "../../hooks/useGetRolesQuery";
import { resetPreviousPath } from "../../redux/slices/previousPath";
import { resetUpdateCourseData } from "../../redux/slices/school/course";
import { resetUpdateDepartamentData } from "../../redux/slices/school/departament";
import { resetUpdateFacultyData } from "../../redux/slices/school/faculty";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createCta } from "../../services/school/cta/createCta";
import { updateManager } from "../../services/school/manager/updateManager";
import { addUserRole } from "../../services/user/addUserRole";
import { createUser } from "../../services/user/createUser";
import { getUserByBI, getUserByNuit } from "../../services/user/getUsers";
import { removeUserRole } from "../../services/user/removeUserRole";
import { getAllStaticRoles } from "../../staticRoles";
import { CtaType } from "../../types/Cta";
import { UserType } from "../../types/User";
import { AcademicLevels, Countries, Country, TypeContract, TypeContractRelashionship } from "../../utils/codes";
import { key_userBi, key_userNuit } from "../../utils/constants";
import { CreateCtaValidationForm } from "./CreateCtaValidationForm";
import { updateCta } from "../../services/school/cta/updateCta";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";

const columnNamesMap: { [key: string]: string } = {
  fullname: "Nome",
  role: "Cargo",
};
export const CreateCta = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteItem, setDeleteItem] = useState<CtaType | null>(null);
  const openDeleteDialog = (s: CtaType) => {
    setDeleteItem(s);
  };
  const confirmDelete = async () => {
    if (deleteItem) {
      await handleDelete(deleteItem!);
    }
  };

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data, status } = useGetUniversitiesQuery({ PageNumber: currentPage, PageSize: 1 });
  const universityData = data?.items?.[0] || { description: "", province: "" };
  const { description, province } = universityData;
  const previousPath = useSelector((state: RootState) => state.previousPath);
  const mutationAddUserRole = useMutation({
    mutationKey: "addUserRole",
    mutationFn: addUserRole,
  });
  const { data: roles } = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });

  const mutationCreateUser = useMutation({
    mutationFn: createUser,
    mutationKey: "createUser",
  });
  const mutation = useMutation(createCta, {
    onSuccess: () => {
      toast.success("CTA criado!");
    },
    onError: () => {
      toast.error("Erro ao criar CTA.");
    },
  });
  const mutationDelete = useMutation(updateCta, {
    onSuccess: () => {
      toast.success("CTA removido!");
    },
    onError: () => {
      toast.error("Erro ao remover CTA.");
    },
  });

  type CtaFormValues = Omit<CtaType, "user"> &
    Omit<CtaType, "idRole"> & {
      user: UserType;
      idRole: string;
    };
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm<CtaFormValues>({
    resolver: yupResolver(CreateCtaValidationForm),
    defaultValues: {
      idUser: "",
      fullname: "",
      searchNuit: "",
      searchBi: "",
      user: {
        bi: "",
        nuit: "",
      },
      idRole: "",
      isCta: true,
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const {
    data: ctas,
    isLoading: loadingCtas,
    refetch: refreshCtas,
    status: statusCtas,
  } = useGetCtasQuery(
    {
      PageSize: 400,
      PageNumber: 1,
    }
  );

  const { isLoading: loadingUserByBi, refetch: fetchUserByBi } = useQuery(
    [key_userBi],
    async () => {
      try {
        const userData = await getUserByBI(getValues("searchBi")!);
        if (!userData) return;
        reset({
          searchBi: getValues("searchBi")!,
          idUser: userData.id,
          fullname: userData.name,
          status: true,
          user: userData,
          isCta: true,
        });
        return userData;
      } catch (error) { }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const { isLoading: loadingUserByNuit, refetch: fetchUserByNuit } = useQuery(
    [key_userNuit],
    async () => {
      try {
        const userData = await getUserByNuit(getValues("searchNuit")!);
        if (!userData) return;
        reset({
          searchBi: getValues("searchBi")!,
          idUser: userData.id,
          fullname: userData.name,
          status: true,
          user: userData,
        });
        return userData;
      } catch (error) { }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.idUser || !data.user?.id) {
        const resultCreateUser = await mutationCreateUser.mutateAsync(
          data.user!
        );
        if (!resultCreateUser) return;
        const { id: idUser } = resultCreateUser as any;
        data.idUser = idUser;
      }
      try {
        await mutationAddUserRole.mutateAsync({
          roleId: data.idRole ?? "",
          userId: data.idUser!,
        });
      } catch (error) {
        console.error(error);
      }
      const result = await mutation.mutateAsync({
        ...data,
        fullname: data.user.name,
      });
      if (result) {
        refreshCtas();
        reset({
          idUser: "",
          user: undefined,
          idRole: "",
          role: "",
        });
      }
    } catch (err: any) { }
  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handleDelete = async (cta: any) => {
    cta.status = false;
    await mutationDelete.mutateAsync(cta);
    try {
      await removeUserRole({
        userId: cta.idUser,
        roleId: cta.idRole,
      });
    } catch (ex) {
      console.error(ex);
    }
    refreshCtas();
  };
  const options =
    ctas?.items.map(() => [
      {
        label: "Excluir",
        onClick: (manager: any) => openDeleteDialog(manager),
        icon: <TrashIcon />,
      },
    ]) || [];

  const handleBIChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue("searchBi", trimmedValue);
      if (trimmedValue.length >= 10) {
        fetchUserByBi();
      } else {
      }
    }, 300),
    [setValue, fetchUserByBi]
  );
  const handleNUITChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue("searchNuit", trimmedValue);
      if (trimmedValue.length >= 8) {
        fetchUserByNuit();
      } else {
      }
    }, 300),
    [setValue, fetchUserByNuit]
  );

  const handleEmailChange = (e: any) => {
    const email = e.target.value;
    setValue("user.email", email);
    setValue("user.userName", email);
  };

  const handleFullnameChange = (e: any) => {
    const fullname = e.target.value;
    setValue("user.name", fullname);
    setValue("fullname", fullname);
  };
  const HandleRoleChange = (idRole: string) => {
    setValue("idRole", idRole);
    const role = roles?.items.find((r) => r.id === idRole)?.name;
    setValue("role", role);
  };
  const handleBack = () => {
    dispatch(resetUpdateFacultyData());
    dispatch(resetUpdateCourseData());
    dispatch(resetUpdateDepartamentData());
    const path = previousPath.previousPath!;
    dispatch(resetPreviousPath());
    navigate(path);
  };
  const filteredRoles = roles?.items.filter(
    (role) => !getAllStaticRoles().includes(role.name!)
  );
  return (
    <Card size="3">
      <Flex direction="row" gap="6">
        <Flex direction="column" gap="5" style={{ flex: 1 }}>

          <Flex align="center">
            <Badge variant="surface" >{province}</Badge>
            <Separator orientation="horizontal" />
            <Badge variant="outline" >{description}</Badge>
          </Flex>

          <form onSubmit={onSubmit} noValidate>
            <Flex direction="column" gap="4">
              <Flex gap="2">
                <TextFieldComponent
                  style={{ width: "100%" }}
                  name="searchBi"
                  label="Procurar por BI"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Procurar por BI",
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleBIChange(e.target.value),
                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      handleBIChange(pastedText);
                    },
                  }}
                />
                <TextFieldComponent
                  style={{ width: "100%" }}
                  name="searchNuit"
                  label="Procurar por NUIT"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Procurar por NUIT",
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      handleNUITChange(e.target.value),
                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      handleNUITChange(pastedText);
                    },
                  }}
                />
              </Flex>
              <Flex gap="8">
                <TextFieldComponent
                  name="user.name"
                  composedName={{ parent: "user", prop: "name" }}
                  label="Nome"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Nome completo",
                    onChange: handleFullnameChange,
                    type: "text",
                  }}
                  style={{ width: "100%" }}
                  required={true}
                />
                <TextFieldComponent
                  name="user.userName"
                  composedName={{ parent: "user", prop: "userName" }}
                  label="Utilizador"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "Utilizador",
                    disabled: "disabled",
                  }}
                  style={{ width: "100%" }}
                  required={true}
                />
                <TextFieldComponent
                  name="user.email"
                  composedName={{ parent: "user", prop: "email" }}
                  label="Email"
                  control={control}
                  errors={errors}
                  fieldProps={{
                    placeholder: "seu@email.com",
                    onChange: handleEmailChange,
                    type: "email",
                  }}
                  style={{ width: "100%" }}
                  required={true}
                />
              </Flex>
              <Flex gap="8">
                <TextFieldComponent
                  name="user.bi"
                  composedName={{ parent: "user", prop: "bi" }}
                  label="BI"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "BI" }}
                  style={{ width: "100%" }}
                  required={true}
                />
                <TextFieldComponent
                  name="user.nuit"
                  composedName={{ parent: "user", prop: "nuit" }}
                  label="NUIT"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Seu NUIT" }}
                  style={{ width: "100%" }}
                  required={true}
                />
                <TextFieldComponent
                  name="user.phoneNumber"
                  composedName={{ parent: "user", prop: "phoneNumber" }}
                  label="Telefone"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "82xxxxxxx" }}
                  style={{ width: "100%" }}
                  required={true}
                />
                
            <TextFieldComponent
              name="user.secondaryPhoneNumber"
              composedName={{ parent: "user", prop: "secondaryPhoneNumber" }}
                  label="Telefone"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "82xxxxxxx" }}
                  style={{ width: "100%" }}
            />
              </Flex>
              <Flex gap="8">
                <DateFieldComponent
                  name="user.dateOfBirth"
                  composedName={{ parent: "user", prop: "dateOfBirth" }}
                  label="Data de Nascimento"
                  control={control}
                  errors={errors}
                  style={{ width: "32%" }}
                  required={true}
                />
                <RadioGroupComponent
                  name="user.gender"
                  composedName={{ parent: "user", prop: "gender" }}
                  label="Gênero"
                  control={control}
                  errors={errors}
                  style={{ width: "68%" }}
                  options={[
                    { value: "M", label: "Masculino" },
                    { value: "F", label: "Feminino" },
                  ]}
                  fieldProps={{
                    style: {
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    },
                  }}
                />
              </Flex>
              <Flex gap="6">
                <SelectSearchableComponent
                  name="country"
                  label="País"
                  control={control}
                  errors={errors}
                  options={Countries?.map((country: Country) => ({
                    value: country.code,
                    label: country.label ?? "",
                  }))}
                  style={{ width: "100%" }}
                />
              </Flex>
              <Flex>
                <SelectComponent
                  name="idRole"
                  label="Perfil"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={filteredRoles?.map((role) => ({
                    value: role.id ?? "",
                    label: role.name ?? "",
                  }))}
                  onSelect={HandleRoleChange}
                  required={true}
                />
              </Flex>

              <Flex gap='4'>

                <SelectComponent
                  required={true}
                  name="typeContract"
                  label="Regime de Contratação"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'typeContract' }}
                  options={TypeContract?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />

                <SelectComponent
                  required={true}
                  name="typeContractRelashionship"
                  label="Regime de Contratação"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'typeContractRelashionship' }}
                  options={TypeContractRelashionship?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />


              </Flex>

              <Flex gap='4'>
                <SelectComponent
                  required={true}
                  name="academicLevel"
                  label="Nível de formação"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'academicLevel' }}
                  options={AcademicLevels?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />
              </Flex>

              <Flex gap='4'>
                <SelectComponent
                  required={true}
                  name="academicLevelEntry"
                  label="Nível de formação - Entrada"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'academicLevelEntry' }}
                  options={AcademicLevels?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />

                <SelectComponent
                  required={true}
                  name="academicLevelActual"
                  label="Nível de formação - Actual"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'academicLevelActual' }}
                  options={AcademicLevels?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />

                <SelectComponent
                  required={true}
                  name="academicLevelDeparture"
                  label="Nível de formação - Saída"
                  control={control}
                  errors={errors}
                  composedName={{ parent: "cta", prop: 'academicLevelDeparture' }}
                  options={AcademicLevels?.map((t: { id: any, description: any }) => ({
                    value: t.id,
                    label: t.description ?? "",
                  }))} style={{ width: '100%' }}
                />
              </Flex>

              <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                <Button
                  color="red"
                  onClick={(e) => {
                    e.preventDefault();
                    handleBack();
                  }}
                >
                  Sair
                </Button>
                <Button
                  type="submit"
                  variant="surface"
                  disabled={mutation.isLoading}
                >
                  {mutation.isLoading ? <Spinner loading /> : "Vincular"}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
        <Flex direction="column" gap="4" style={{ flex: 1 }}>
          <Lists
            status={statusCtas}
            TableComponent={() => (
              <TableComponent
                columns={Object.keys(columnNamesMap)}
                items={ctas?.items}
                options={options}
                columnNamesMap={columnNamesMap}
                totalPages={ctas?.totalPages || 1}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            )}
            handleClickCreate={undefined}
          />
          <ConfirmAlertDialog
            open={!!deleteItem}
            onOpenChange={(open) => !open && setDeleteItem(null)}
            title="Esta operação é irreversível"
            item={deleteItem}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteItem(null)}
          />
        </Flex>
      </Flex>
    </Card>
  );
};